/**
 * Create DOM element with attributes
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes?: Record<string, string>,
  children?: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);

  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'className') {
        element.className = value;
      } else if (key.startsWith('data-') || key.startsWith('aria-')) {
        element.setAttribute(key, value);
      } else {
        (element as any)[key] = value;
      }
    });
  }

  if (children) {
    children.forEach((child) => {
      if (typeof child === 'string') {
        element.appendChild(document.createTextNode(child));
      } else {
        element.appendChild(child);
      }
    });
  }

  return element;
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Validate URL to prevent XSS via javascript: or data: protocols
 * Only allows http:, https:, and blob: protocols
 * blob: URLs are safe - they're generated by the browser for local files
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return ['http:', 'https:', 'blob:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * Sanitize URL - returns safe URL or empty string
 */
export function sanitizeUrl(url: string): string {
  return isValidUrl(url) ? url : '';
}

/**
 * Parse simple markdown (bold, italic, links, code)
 * URLs are validated to prevent XSS via javascript: protocol
 */
export function parseMarkdown(text: string): string {
  return escapeHtml(text)
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Code
    .replace(/`(.+?)`/g, '<code>$1</code>')
    // Links - validate URL to prevent XSS
    .replace(/\[(.+?)\]\((.+?)\)/g, (_match, linkText, url) => {
      const safeUrl = sanitizeUrl(url);
      if (safeUrl) {
        return `<a href="${safeUrl}" target="_blank" rel="noopener">${linkText}</a>`;
      }
      // If URL is invalid, just show the text without link
      return linkText;
    })
    // Line breaks
    .replace(/\n/g, '<br>');
}

/**
 * Format timestamp
 */
export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Scroll element to bottom
 */
export function scrollToBottom(element: HTMLElement, smooth = true): void {
  element.scrollTo({
    top: element.scrollHeight,
    behavior: smooth ? 'smooth' : 'auto',
  });
}

/**
 * Check if element is scrolled to bottom
 */
export function isScrolledToBottom(element: HTMLElement, threshold = 50): boolean {
  return element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
}

/**
 * Generate unique ID
 */
export function generateId(): string {
  return 'cevro_' + Math.random().toString(36).substring(2, 9);
}
